import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { StatusType } from '@datadog/browser-logs';
import { isJupiterLite } from 'constants/Global.constants';
import {
  isPublicPath,
  shouldSkipAuthRedirect,
} from 'constants/PublicPaths.constants';
import {
  setAuthStatus,
  setGetAuthToken,
  setIsFirstLogin,
  setLogin,
  setLogout,
  setShowUnauthorised,
  setUser,
} from 'ducks/auth/authSlice';
import { PROJECT_ROUTE_PATTERN } from 'routes/routes.constants';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';

import api from 'api';
import { HTTPError } from 'api/error';
import { LOGGER_TYPE } from 'api/types';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { logToDataDog } from 'lib/ddlogHelper';
import { trackPendoEvent } from 'lib/pendo';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import { getOTAuth0Params } from 'screens/OTLogin/utils';
import { detectInAppBrowser } from 'utils/inAppBrowser';

export const Auth = ({ children }: PropsWithChildren) => {
  const { accountId, projectId } = useProjectRoute();
  const dispatch = useLocalDispatch();
  const toast = useToast();
  const authStatus = useAppSelector(state => state.auth.authStatus);
  const [shouldVerifyEmail, setShouldVerifyEmail] = useState(false);
  const pathname = usePathname();
  const hasRedirected = useRef(false);

  const isPublic = isPublicPath(pathname);
  // Google OAuth rejects requests from in-app browsers (LinkedIn, Facebook,
  // Instagram, etc.) with `error=disallowed_useragent`. Block the auto-redirect
  // and instruct the user to switch to their default browser.
  const inAppBrowser = useMemo(() => detectInAppBrowser(), []);

  const {
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    user,
  } = useAuth0();

  const login = useCallback(
    async (path: string) => {
      if (!isAuthenticated) {
        if (hasRedirected.current) {
          return;
        }
        hasRedirected.current = true;

        // Add ext-brand=opentable to Auth0 params if user is an OT user for a
        // separate Auth0 log in screen.
        const otUserAuthParams = getOTAuth0Params();

        try {
          await loginWithRedirect({
            appState: {
              returnTo: path,
            },
            authorizationParams: otUserAuthParams,
          });
        } catch (e) {
          console.error('Error occurred while redirecting to login', e);
          hasRedirected.current = false;
        }
      }
    },
    [isAuthenticated, loginWithRedirect],
  );

  const authoriseRetryCount = useRef(0);
  const authoriseRetryTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const MAX_AUTHORISE_RETRIES = 3;
  const [authoriseError, setAuthoriseError] = useState(false);

  const handleFirstLogin = useCallback(() => {
    dispatch(setIsFirstLogin(true));
    try {
      const visitorId = window.pendo?.getVisitorId?.() ?? '';
      trackPendoEvent('workspace_user_added', {
        new_user_visitor_id: visitorId,
      });
    } catch (e) {
      console.error('Failed to track workspace_user_added event', e);
    }
  }, [dispatch]);

  const authoriseUser = useCallback(async () => {
    try {
      dispatch(setAuthStatus('pending'));

      const result = await api.authoriseUser();

      // Handle undefined response from 401 redirect
      if (!result) {
        return;
      }

      authoriseRetryCount.current = 0;
      setAuthoriseError(false);
      const { isAuthorised, user } = result;

      dispatch(setAuthStatus(isAuthorised ? 'authorized' : 'unauthorized'));
      dispatch(setShowUnauthorised(!isAuthorised));
      if (user?.is_first_login) {
        handleFirstLogin();
      }
    } catch (error: unknown) {
      logToDataDog(
        LOGGER_TYPE.USER_MANAGEMENT,
        StatusType.error,
        'Error whilst authorising user',
        {
          error,
          attempt: authoriseRetryCount.current + 1,
        },
        accountId,
        projectId,
      );

      // 403 is a definitive "no access" — show unauthorized page immediately
      if (error instanceof HTTPError && error?.httpStatus === 403) {
        dispatch(setAuthStatus('unauthorized'));
        dispatch(setShowUnauthorised(true));
        return;
      }

      // For all other errors (401, 5XX, network): retry with backoff up to
      // MAX_AUTHORISE_RETRIES, then show the error page.
      if (authoriseRetryCount.current < MAX_AUTHORISE_RETRIES) {
        authoriseRetryCount.current += 1;
        // Backoff: 1s, 2s, 3s (cap at 3s)
        const delay = Math.min(1000 * authoriseRetryCount.current, 3000);
        clearTimeout(authoriseRetryTimer.current);
        authoriseRetryTimer.current = setTimeout(() => {
          dispatch(setAuthStatus('not-checked'));
        }, delay);
      } else {
        // Retries exhausted — show error page
        setAuthoriseError(true);
      }
    }
  }, [accountId, dispatch, handleFirstLogin, projectId]);

  const handleLogout = useCallback(async () => {
    if (hasRedirected.current) {
      return;
    }
    hasRedirected.current = true;
    dispatch(setShowUnauthorised(false));
    try {
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } catch {
      toast.error({
        title: 'We could not log you out. Try again!',
      });
      hasRedirected.current = false;
    }
  }, [dispatch, logout, toast]);

  // Effect 1: Store auth methods in Redux
  useEffect(() => {
    dispatch(setLogin(login));
    dispatch(
      setLogout(() => {
        void handleLogout();
      }),
    );
    dispatch(
      setGetAuthToken(async (force?: boolean) => {
        try {
          return await getAccessTokenSilently(
            force ? { cacheMode: 'off' } : undefined,
          );
        } catch (_e) {
          // In an in-app browser the silent auth iframe is doomed to fail; the
          // logout redirect that would normally fire here just bounces back into
          // the warning page in a loop, so skip it.
          if (
            !inAppBrowser &&
            !shouldSkipAuthRedirect(window.location.pathname)
          ) {
            void handleLogout();
          }
        }
      }),
    );
  }, [dispatch, login, handleLogout, getAccessTokenSilently, inAppBrowser]);

  // Effect 2: Handle authentication (login redirect)
  useEffect(() => {
    if (isPublic || isLoading || inAppBrowser) {
      return;
    }

    if (!isAuthenticated || !user) {
      login(window.location.pathname).catch(error => {
        toast.error({
          title: 'We could not log you in. Try again',
        });
        console.error('Error occurred while logging in', error);
      });
    }
  }, [isAuthenticated, user, isLoading, isPublic, inAppBrowser, login, toast]);

  // Effect 3: Handle email verification
  useEffect(() => {
    if (isPublic || isLoading || !isAuthenticated || !user) {
      return;
    }

    if (user && !user?.email_verified && isJupiterLite) {
      setShouldVerifyEmail(true);
    } else {
      setShouldVerifyEmail(false);
    }
  }, [isAuthenticated, user, isLoading, isPublic]);

  // Effect 4: Handle user authorisation
  useEffect(() => {
    if (isPublic || isLoading || !isAuthenticated || !user) {
      return;
    }

    // Don't authorize if email not verified
    if (user && !user?.email_verified && isJupiterLite) {
      return;
    }

    if (authStatus !== 'authorized' && authStatus !== 'pending') {
      void authoriseUser();
    }

    return () => {
      clearTimeout(authoriseRetryTimer.current);
    };
  }, [isAuthenticated, authStatus, user, isLoading, isPublic, authoriseUser]);

  useEffect(() => {
    if (user) {
      dispatch(setUser(user));
    }
  }, [dispatch, user]);

  const isProjectRoute = PROJECT_ROUTE_PATTERN.test(pathname);

  if (!isPublic) {
    if (authoriseError) {
      return <ErrorPage type={ErrorStatus.UNHANDLED_ERROR} />;
    }

    if (shouldVerifyEmail) {
      return <ErrorPage type={ErrorStatus.EMAIL_UNVERIFIED} />;
    }

    if (inAppBrowser && (!isAuthenticated || !user)) {
      return <ErrorPage type={ErrorStatus.IN_APP_BROWSER} />;
    }

    // For project routes, skip showing the Loader here - ProjectLoadingOverlay handles it
    if (!isAuthenticated || !user) {
      if (!isProjectRoute) {
        return <Loader inProjectPage={false} fullScreen={true} logo={true} />;
      }
    }
  }

  return children;
};
